import React from 'react';
import { Card } from '../../ui/Card';

interface ProtocolPageProps {
  protocolName: string;
  tagline: string;
  tierBadge: 'Free' | 'Paid' | 'Application';
  whatIs: string;
  biochemistry: React.ReactNode;
  whoItIsFor: string;
  francoisSays: string;
  ctaText: string;
  onCtaClick?: () => void;
  isLocked?: boolean;
  lockedMessage?: string;
  lockedCtaText?: string;
}

export function ProtocolPage({
  protocolName,
  tagline,
  tierBadge,
  whatIs,
  biochemistry,
  whoItIsFor,
  francoisSays,
  ctaText,
  onCtaClick,
  isLocked = false,
  lockedMessage,
  lockedCtaText,
}: ProtocolPageProps) {
  // Tier badge colors
  const getTierColors = (tier: string) => {
    switch (tier) {
      case 'Free':
        return 'bg-surface text-on-surface';
      case 'Paid':
        return 'bg-primary text-primary-contrast';
      case 'Application':
        return 'bg-error-container text-error';
      default:
        return 'bg-surface text-on-surface';
    }
  };

  const tierColorClass = getTierColors(tierBadge);

  return (
    <div className="space-y-6 p-4 pb-20">
      {/* Consolidated Card */}
      <Card className="border-l-4 border-primary bg-surface-container-low rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${tierColorClass}`}>
              {tierBadge}
            </span>
          </div>
        </div>
        <h1 className="mt-4 text-2xl font-bold text-on-surface">{protocolName}</h1>
        <p className="mt-1 text-sm text-primary">{tagline}</p>

        <div className="mt-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-on-surface mb-3">What It Is</h2>
            <p className="text-sm text-on-surface-variant leading-relaxed">{whatIs}</p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-on-surface mb-3">Biochemistry</h2>
            <div className="text-sm text-on-surface-variant space-y-2">{biochemistry}</div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-on-surface mb-3">Who It Is For</h2>
            <p className="text-sm text-on-surface-variant leading-relaxed">{whoItIsFor}</p>
          </div>
        </div>
      </Card>

      {/* Francois Says */}
      <Card className="border-l-4 border-secondary bg-surface-container rounded-xl p-6">
        <p className="italic text-sm text-secondary leading-relaxed">"{francoisSays}"</p>
      </Card>

      {/* CTA */}
      <div className="flex flex-col items-center justify-center py-4">
        {isLocked ? (
          <Card className="w-full border border-error/30 rounded-xl p-6 text-center">
            <div className="mb-3 flex justify-center">
              <span className="material-symbols-outlined text-error text-4xl">lock</span>
            </div>
            <p className="mb-4 text-sm text-on-surface-variant">{lockedMessage}</p>
            {lockedCtaText && (
              <button
                className="w-full rounded-xl bg-surface-container-highest px-4 py-3 text-sm font-bold text-on-surface-variant"
                onClick={onCtaClick}
              >
                {lockedCtaText}
              </button>
            )}
          </Card>
        ) : (
          <button
            className="w-full rounded-xl bg-primary px-4 py-4 text-base font-bold text-primary-contrast hover:shadow-lg hover:shadow-primary/30"
            onClick={onCtaClick}
          >
            {ctaText}
          </button>
        )}
      </div>
    </div>
  );
}
